import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { 
  ArrowLeft, Save, Send, Plus, X, Loader2, Download, 
  Info, Upload, FileText, CheckCircle, AlertCircle 
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const articleTypes = [
  { value: 'research', label: 'Recherche empirique' },
  { value: 'case_study', label: 'Étude de cas' },
  { value: 'review', label: 'Revue de littérature' },
  { value: 'practice_note', label: 'Note de pratique' },
  { value: 'protocol', label: 'Protocole' }
];

const ACCEPTED_FORMATS = '.docx,.doc,.tex,.latex,.zip';

export default function ArticleEditorPage() {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const isNew = articleId === 'new';
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    title: '',
    abstract: '',
    content: '',
    keywords: [],
    article_type: 'research',
    references: [],
    allow_download: false,
    manuscript_file_id: null
  });
  const [manuscriptFile, setManuscriptFile] = useState(null);
  const [newKeyword, setNewKeyword] = useState('');
  const [newReference, setNewReference] = useState('');

  useEffect(() => {
    if (!isNew) {
      fetchArticle();
    }
  }, [articleId]);

  const fetchArticle = async () => {
    try {
      const res = await fetch(`${API_URL}/api/articles/${articleId}`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (res.ok) {
        const article = await res.json();
        setForm({
          title: article.title,
          abstract: article.abstract,
          content: article.content || '',
          keywords: article.keywords || [],
          article_type: article.article_type,
          references: article.references || [],
          allow_download: article.allow_download || false,
          manuscript_file_id: article.manuscript_file_id || null
        });
        if (article.manuscript_file_id) {
          setManuscriptFile({ 
            file_id: article.manuscript_file_id, 
            name: 'Manuscrit téléversé', 
            uploaded: true 
          });
        }
      } else {
        navigate('/dashboard');
      }
    } catch (e) {
      toast.error('Erreur lors du chargement');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const ext = file.name.split('.').pop().toLowerCase();
    if (!['docx', 'doc', 'tex', 'latex', 'zip'].includes(ext)) {
      toast.error('Format non supporté. Utilisez Word (.docx) ou LaTeX (.tex).');
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 50 Mo)');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const headers = getAuthHeaders();
      delete headers['Content-Type'];

      const res = await fetch(`${API_URL}/api/files/upload`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        setManuscriptFile({ 
          file_id: data.file_id, 
          name: file.name, 
          size: data.size,
          uploaded: true 
        });
        setForm(prev => ({ ...prev, manuscript_file_id: data.file_id }));
        toast.success('Manuscrit téléversé avec succès');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur lors du téléversement');
      }
    } catch (e) {
      toast.error('Erreur lors du téléversement');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (submit = false) => {
    if (!form.title.trim() || !form.abstract.trim()) {
      toast.error('Veuillez remplir le titre et le résumé');
      return;
    }

    if (!form.manuscript_file_id && !form.content.trim()) {
      toast.error('Veuillez téléverser un manuscrit ou rédiger le contenu');
      return;
    }

    setSaving(true);
    try {
      let res;
      if (isNew) {
        res = await fetch(`${API_URL}/api/articles`, {
          method: 'POST',
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify(form)
        });
      } else {
        res = await fetch(`${API_URL}/api/articles/${articleId}`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify(form)
        });
      }

      if (res.ok) {
        const article = await res.json();
        
        if (submit) {
          const submitRes = await fetch(`${API_URL}/api/articles/${article.article_id}/submit`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include'
          });
          if (submitRes.ok) {
            toast.success('Article soumis pour évaluation');
          }
        } else {
          toast.success(isNew ? 'Article créé' : 'Article sauvegardé');
        }
        
        navigate('/dashboard');
      } else {
        throw new Error('Save failed');
      }
    } catch (e) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const addKeyword = () => {
    if (newKeyword.trim() && !form.keywords.includes(newKeyword.trim())) {
      setForm({ ...form, keywords: [...form.keywords, newKeyword.trim()] });
      setNewKeyword('');
    }
  };

  const removeKeyword = (keyword) => {
    setForm({ ...form, keywords: form.keywords.filter(k => k !== keyword) });
  };

  const addReference = () => {
    if (newReference.trim()) {
      setForm({ ...form, references: [...form.references, newReference.trim()] });
      setNewReference('');
    }
  };

  const removeReference = (index) => {
    setForm({ ...form, references: form.references.filter((_, i) => i !== index) });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="article-editor-page" className="min-h-screen py-8 px-6 md:px-12">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button 
            onClick={() => navigate('/dashboard')} 
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </button>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => handleSave(false)} 
              disabled={saving}
              className="gap-2"
              data-testid="save-draft-btn"
            >
              <Save className="w-4 h-4" />
              Sauvegarder
            </Button>
            <Button 
              onClick={() => handleSave(true)} 
              disabled={saving}
              className="gap-2"
              data-testid="submit-btn"
            >
              <Send className="w-4 h-4" />
              Soumettre
            </Button>
          </div>
        </div>

        <h1 className="font-serif text-3xl font-bold mb-8">
          {isNew ? 'Nouvel article' : 'Modifier l\'article'}
        </h1>

        <div className="space-y-6">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Titre *</Label>
            <Input
              id="title"
              placeholder="Titre de votre article"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="text-lg"
              data-testid="title-input"
            />
          </div>

          {/* Type */}
          <div className="space-y-2">
            <Label>Type d'article</Label>
            <Select 
              value={form.article_type} 
              onValueChange={(value) => setForm({ ...form, article_type: value })}
            >
              <SelectTrigger data-testid="type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {articleTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Keywords */}
          <div className="space-y-2">
            <Label>Mots-clés</Label>
            <div className="flex gap-2">
              <Input
                placeholder="Ajouter un mot-clé"
                value={newKeyword}
                onChange={(e) => setNewKeyword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                data-testid="keyword-input"
              />
              <Button type="button" variant="outline" onClick={addKeyword}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {form.keywords.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {form.keywords.map((keyword, idx) => (
                  <Badge key={idx} variant="secondary" className="gap-1">
                    {keyword}
                    <button onClick={() => removeKeyword(keyword)} className="ml-1 hover:text-destructive">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Abstract */}
          <div className="space-y-2">
            <Label htmlFor="abstract">Résumé *</Label>
            <Textarea
              id="abstract"
              placeholder="Résumé de votre article (150-250 mots)"
              value={form.abstract}
              onChange={(e) => setForm({ ...form, abstract: e.target.value })}
              rows={5}
              data-testid="abstract-textarea"
            />
          </div>

          {/* Manuscript Upload */}
          <Card className="border-primary/20" data-testid="manuscript-upload-card">
            <CardHeader>
              <CardTitle className="font-serif text-lg flex items-center gap-2">
                <Upload className="w-5 h-5" />
                Manuscrit
              </CardTitle>
              <CardDescription>
                Téléversez votre manuscrit au format Word (.docx) ou LaTeX (.tex). 
                Utilisez le <a href="/JPRP-template-manuscrit.docx" download className="text-primary underline hover:text-primary/80">template JPRP</a> pour la mise en forme.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_FORMATS}
                onChange={handleFileUpload}
                className="hidden"
                data-testid="file-input"
              />

              {manuscriptFile?.uploaded ? (
                <div className="border border-primary/30 rounded-lg p-4 bg-primary/5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-primary" />
                      <div>
                        <p className="font-medium text-sm">{manuscriptFile.name}</p>
                        {manuscriptFile.size && (
                          <p className="text-xs text-muted-foreground">
                            {formatFileSize(manuscriptFile.size)}
                          </p>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      data-testid="replace-file-btn"
                    >
                      Remplacer
                    </Button>
                  </div>
                </div>
              ) : (
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-muted-foreground/30 rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
                  data-testid="upload-dropzone"
                >
                  {uploading ? (
                    <div className="flex flex-col items-center gap-3">
                      <Loader2 className="w-8 h-8 animate-spin text-primary" />
                      <p className="text-sm text-muted-foreground">Téléversement en cours...</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-3">
                      <FileText className="w-8 h-8 text-muted-foreground" />
                      <div>
                        <p className="font-medium">Cliquez pour téléverser votre manuscrit</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Word (.docx), LaTeX (.tex) ou archive (.zip) — Max 50 Mo
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-4 p-3 bg-muted/50 rounded-lg flex items-start gap-2">
                <Info className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                <p className="text-xs text-muted-foreground">
                  Le manuscrit doit respecter les <a href="/author-guidelines" className="text-primary underline">instructions aux auteurs</a>. 
                  Téléchargez le <a href="/JPRP-template-manuscrit.docx" download className="text-primary underline">template Word JPRP</a> pour 
                  garantir une mise en forme conforme.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Download Permission */}
          <Card>
            <CardHeader>
              <CardTitle className="font-serif text-lg flex items-center gap-2">
                <Download className="w-5 h-5" />
                Options de téléchargement
              </CardTitle>
              <CardDescription>
                Contrôlez si les lecteurs peuvent télécharger votre article
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="font-medium">Autoriser le téléchargement</p>
                  <p className="text-sm text-muted-foreground">
                    Les lecteurs pourront télécharger votre article
                  </p>
                </div>
                <Switch
                  checked={form.allow_download}
                  onCheckedChange={(checked) => setForm({ ...form, allow_download: checked })}
                  data-testid="allow-download-switch"
                />
              </div>
            </CardContent>
          </Card>

          {/* References */}
          <Card>
            <CardHeader>
              <CardTitle className="font-serif text-lg">Références bibliographiques</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  placeholder="Ajouter une référence (format APA)"
                  value={newReference}
                  onChange={(e) => setNewReference(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addReference())}
                  data-testid="reference-input"
                />
                <Button type="button" variant="outline" onClick={addReference}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {form.references.length > 0 && (
                <ol className="list-decimal list-inside space-y-2">
                  {form.references.map((ref, idx) => (
                    <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                      <span className="flex-1">{ref}</span>
                      <button 
                        onClick={() => removeReference(idx)} 
                        className="text-destructive hover:text-destructive/80"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
                </ol>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
